import React, { useRef } from "react";
import { Tabs } from "@base-ui/react/tabs";
import "./Settings.scss";
import { Scrollbars } from "react-custom-scrollbars-2";
import classNames from "classnames";
import { HOTKEYS_SCOPES } from "@hooks/keyboard/useKeyboardCommand";
import { useEntities } from "@hooks/useEntities";
import { useScopeSwitch } from "@hooks/keyboard/useScopeSwitch";
import { useScope } from "@state/scopeContext.lib";
import { useCanPerformOrganizationAction } from "@hooks/permissions/organization/useCanPerformOrganizationAction";
import { OrganizationAction } from "@common/userOrganizationRoles";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { LockIcon, NotificationsIcon, ProfileIcon, RobotIcon } from "@icons";
import { NavigationItem } from "@design-system/Navigation/NavigationItem";
import { NavigationMenu } from "@design-system/Navigation/NavigationMenu";
import { CommandsProvider } from "../commands/CommandsProvider";
import { McpCliSettings } from "./McpCli/McpCliSettings";
import { GeneralSettings } from "./General/GeneralSettings";
import { SecuritySettings } from "./Security/SecuritySettings";
import { useSettings } from "./SettingsProvider";
import { EntityPanes } from "./EntityPanes";
import { EntitySettingsNavigation } from "./EntitySettingsNavigation";
import { SettingsNavLink } from "./SettingsNavLink";
import "./SettingsWidget.scss";
import { WorkspaceSettingsNavigation } from "./WorkspaceSettingsNavigation";
import { WorkspacePanes } from "./WorkspacePanes";
import { OrganizationSettingsNavigation } from "./OrganizationSettingsNavigation";
import { OrganizationPanes } from "./OrganizationPanes";
import { NotificationsSettings } from "./Notifications/NotificationsSettings";
import { UserSettingsPages } from "./constants";
import { SettingsTabPane } from "./SettingsTabPane";

export const Settings: React.FC = () => {
    useScopeSwitch(HOTKEYS_SCOPES.SETTINGS_MODAL);
    const { changePage, path } = useSettings();
    const entities = useEntities();
    const { workspaceId } = useScope();
    const canManageOrganizationSettings = useCanPerformOrganizationAction(
        OrganizationAction.MANAGE_ORGANIZATION_SETTINGS,
    );
    const { personalAccessTokens: patsFlagEnabled } = useTypedFlags();

    const scrollbarsRef = useRef<Scrollbars>(null);

    return (
        <Tabs.Root
            render={<div />}
            className={classNames("settings", `settings--${path[0]}`)}
            orientation="vertical"
            value={path[0] ?? UserSettingsPages.GENERAL}
            onValueChange={(value) => changePage([value])}
        >
            <CommandsProvider>
                <aside className="settings__navigation">
                    <Scrollbars>
                        <Tabs.List
                            render={<div />}
                            className="settings__navigation__content"
                        >
                            <header className="settings__navigation__header">
                                My Account
                            </header>

                            <NavigationMenu className="flex-column">
                                <SettingsNavLink
                                    eventKey={UserSettingsPages.GENERAL}
                                >
                                    <NavigationItem icon={<ProfileIcon />}>
                                        Profile
                                    </NavigationItem>
                                </SettingsNavLink>
                                <SettingsNavLink
                                    eventKey={UserSettingsPages.NOTIFICATIONS}
                                >
                                    <NavigationItem
                                        icon={<NotificationsIcon />}
                                    >
                                        Notifications
                                    </NavigationItem>
                                </SettingsNavLink>
                                <SettingsNavLink
                                    eventKey={UserSettingsPages.SECURITY}
                                >
                                    <NavigationItem icon={<LockIcon />}>
                                        Security
                                    </NavigationItem>
                                </SettingsNavLink>
                                {patsFlagEnabled && (
                                    <SettingsNavLink
                                        eventKey={UserSettingsPages.MCP_CLI}
                                    >
                                        <NavigationItem
                                            icon={<RobotIcon />}
                                            data-testid="settings-nav-mcp-cli"
                                        >
                                            Connectors
                                        </NavigationItem>
                                    </SettingsNavLink>
                                )}
                            </NavigationMenu>

                            {workspaceId && (
                                <>
                                    <WorkspaceSettingsNavigation />
                                    <EntitySettingsNavigation
                                        entities={entities}
                                    />
                                </>
                            )}
                            {!workspaceId && canManageOrganizationSettings && (
                                <OrganizationSettingsNavigation />
                            )}
                        </Tabs.List>
                    </Scrollbars>
                </aside>

                <div className="settings__content__panel">
                    <Scrollbars ref={scrollbarsRef}>
                        <main className="settings__content">
                            <SettingsTabPane
                                eventKey={UserSettingsPages.GENERAL}
                            >
                                <GeneralSettings />
                            </SettingsTabPane>

                            <SettingsTabPane
                                eventKey={UserSettingsPages.NOTIFICATIONS}
                            >
                                <NotificationsSettings />
                            </SettingsTabPane>

                            <SettingsTabPane
                                eventKey={UserSettingsPages.SECURITY}
                            >
                                <SecuritySettings />
                            </SettingsTabPane>

                            {patsFlagEnabled && (
                                <SettingsTabPane
                                    eventKey={UserSettingsPages.MCP_CLI}
                                >
                                    <McpCliSettings />
                                </SettingsTabPane>
                            )}

                            {workspaceId && (
                                <WorkspacePanes scrollbars={scrollbarsRef} />
                            )}
                            {!workspaceId && canManageOrganizationSettings && (
                                <OrganizationPanes />
                            )}

                            {entities.map((entity) => (
                                <EntityPanes
                                    entity={entity}
                                    scrollbars={scrollbarsRef}
                                    key={entity.id}
                                />
                            ))}
                        </main>
                    </Scrollbars>
                </div>
            </CommandsProvider>
        </Tabs.Root>
    );
};
